import { Link } from "react-router-dom"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { PolicyHeader } from "@/components/shared"
import { companyDetails } from "@/lib/company-details"

const effectiveDate = "April 12, 2026"

const sections = [
  {
    id: "relationship",
    title: "Relationship to the Terms of Use",
    content:
      "These Monra eSIM Service Terms apply specifically to the purchase and use of Monra eSIM connectivity services. They supplement Monra's general Terms of Use, Privacy Policy, and Refund Policy. If these Monra eSIM Service Terms conflict with the general Terms of Use, these product-specific terms control for Monra eSIM.",
  },
  {
    id: "contracting-party",
    title: "Contracting Party",
    content:
      `${companyDetails.legalName}, trading as Monra, is the contracting party for Monra eSIM unless a specific checkout flow, invoice, or service notice expressly states otherwise.`,
  },
  {
    id: "service-overview",
    title: "Service Overview",
    content:
      "Monra eSIM provides digital eSIM-enabled connectivity products and related services through platform, carrier, roaming, and provisioning partners. Monra eSIM plans are data-only unless Monra expressly states otherwise in writing for a specific product. Service characteristics may vary by destination, device, plan, partner network, and local conditions.",
  },
  {
    id: "accounts",
    title: "Accounts and Security",
    content:
      "You are responsible for maintaining accurate account information and for all activity that occurs under your account. You must keep credentials, QR codes, activation details, and device access secure.",
    bullets: [
      "Provide accurate, current, and complete registration and billing information.",
      "Keep your password and access credentials confidential and do not share your account.",
      "Notify Monra promptly if you believe your account, email, device, or activation details have been compromised.",
      "You remain responsible for activity under your account unless and until you notify Monra of unauthorized use.",
    ],
  },
  {
    id: "orders",
    title: "Orders, Activation, and Plan Validity",
    content:
      "When you place an order for Monra eSIM, you are requesting immediate digital delivery of an eSIM product. Order details shown at checkout or in the app, including destination, data allowance, validity period, pricing, and plan limitations, form part of the contract for that purchase.",
    bullets: [
      "An eSIM may be delivered through a QR code, in-app installation flow, manual activation data, or another digital provisioning method.",
      "You authorize Monra to begin performance of the service immediately once digital delivery or activation details are made available to you.",
      "Unless checkout or the app expressly states otherwise, plan activation and validity begin when your device first connects to a supported network in the purchased destination.",
      "Unused data, days, or service entitlements expire when the applicable validity period ends, unless local law requires otherwise.",
    ],
  },
  {
    id: "payments",
    title: "Pricing, Taxes, and Payment",
    content:
      "You agree to pay all charges associated with your Monra eSIM purchase, including applicable taxes, fees, and surcharges displayed at the time of checkout. Prices may change at any time for future purchases and may vary by destination, plan type, promotions, or local tax rules.",
    bullets: [
      "You authorize Monra and its payment providers to charge the selected payment method for all approved purchases and related amounts due.",
      "Monra may reject or place a hold on orders that appear fraudulent, violate sanctions or telecom compliance rules, or cannot be fulfilled safely.",
      "If a payment is reversed, charged back, or disputed without proper basis, Monra may suspend your account or future purchases while the matter is reviewed.",
    ],
  },
  {
    id: "compatibility",
    title: "Device Compatibility and Customer Responsibilities",
    content:
      "You are responsible for confirming that your device, destination, and usage needs match the plan you buy. Monra eSIM products can fail or underperform if your device is locked, unsupported, outdated, incorrectly configured, or used in a jurisdiction with restrictions.",
    bullets: [
      "Your device must be carrier-unlocked, eSIM-compatible, and appropriately configured for the purchased plan.",
      "You are responsible for choosing the correct destination, plan type, and activation timing before purchase.",
      "You must follow installation instructions, maintain backups where appropriate, and keep your device software reasonably up to date.",
      "You must comply with local laws, export controls, sanctions, telecom registration rules, and restrictions on SIM use in the countries where you use the service.",
      "Because Monra eSIM plans are data-only unless stated otherwise, if you need voice, SMS, or emergency access through a traditional mobile line, you are responsible for maintaining that service separately.",
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    content:
      "You may use Monra eSIM only for lawful, authorized, and ordinary consumer or approved business purposes. You may not misuse the service, interfere with network operations, or use Monra eSIM to support abusive or unlawful conduct.",
    bullets: [
      "Do not resell, sublicense, clone, or redistribute eSIM profiles, activation data, or access credentials unless Monra has expressly authorized it in writing.",
      "Do not use the service for fraud, spam, unlawful surveillance, network abuse, bulk automation, or other prohibited conduct.",
      "Do not attempt to bypass plan restrictions, fair-use controls, country limitations, or technical protection measures.",
      "Do not reverse engineer, scrape, interfere with, or overload Monra's systems, websites, apps, APIs, or provisioning processes.",
    ],
  },
  {
    id: "third-party-networks",
    title: "Third-Party Networks and Service Availability",
    content:
      "Monra eSIM relies on third-party carriers, roaming partners, infrastructure providers, payment processors, and platform vendors. Coverage maps, partner lists, supported countries, speeds, and service quality are estimates or current-service descriptions, not guarantees.",
    bullets: [
      "Coverage, speed, latency, and availability vary by partner network, local conditions, buildings, terrain, weather, congestion, regulations, and your device.",
      "Monra does not guarantee uninterrupted service, minimum speeds, or error-free availability in every location or at every time.",
      "Monra may change, suspend, or discontinue plans, countries, partner networks, or features at any time for operational, legal, security, fraud, compliance, or commercial reasons.",
      "Monra is not responsible for failures caused by third-party networks, device manufacturers, app stores, internet access providers, or events outside Monra's reasonable control.",
    ],
  },
  {
    id: "suspension",
    title: "Suspension and Termination",
    content:
      "Monra may suspend, restrict, or terminate access to Monra eSIM, plans, or accounts at any time if Monra reasonably believes there has been a violation of these terms, fraud, abuse, chargeback activity, sanctions exposure, regulatory risk, or conduct that could harm Monra, users, partners, or networks.",
    bullets: [
      "Monra may suspend service while investigating suspected fraud, unauthorized access, chargebacks, or compliance issues.",
      "Termination does not eliminate your obligation to pay valid charges incurred before termination.",
      "Monra may retain account and transaction records as required for legal, regulatory, security, audit, or dispute-resolution purposes.",
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    content:
      "To the maximum extent permitted by law, Monra eSIM is provided on an \"as is\" and \"as available\" basis. Monra disclaims all express or implied warranties not expressly stated in these terms, including implied warranties of merchantability, fitness for a particular purpose, non-infringement, availability, compatibility, or uninterrupted operation. Monra does not guarantee that a plan will be available in every location, on every device, or without interruption.",
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    content:
      "To the maximum extent permitted by law, Monra and its operators, personnel, contractors, service providers, network partners, and vendors will not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including loss of profit, goodwill, data, business opportunity, or expected savings, arising from or related to Monra eSIM. To the maximum extent permitted by law, Monra's total aggregate liability for any claim relating to a specific Monra eSIM plan or transaction will not exceed the amount you paid to Monra for the plan or transaction giving rise to the claim during the twelve months before the event giving rise to the claim.",
  },
  {
    id: "indemnity",
    title: "Indemnification",
    content:
      "You agree to defend, indemnify, and hold harmless Monra and its operators, personnel, contractors, service providers, network partners, and vendors from and against claims, liabilities, damages, judgments, awards, losses, costs, and expenses arising out of or related to your misuse of Monra eSIM, your violation of these terms, your violation of law, your infringement of another party's rights, or your use of the service in a way that causes harm to Monra or a third party.",
  },
  {
    id: "changes",
    title: "Changes to Monra eSIM and These Terms",
    content:
      "Monra may update these Monra eSIM Service Terms, change plan availability, change prices for future purchases, or modify service features from time to time. When required, Monra will post updated terms, revise the effective date, or provide additional notice. Your continued use of Monra eSIM after updated terms take effect means you accept the updated terms for future use.",
  },
  {
    id: "contact",
    title: "Contact Us",
    content:
      "For questions about Monra eSIM Service Terms, contact Monra support.",
    email: companyDetails.contactEmail,
  },
]

export default function EsimServiceTerms() {
  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Navbar links={[]} logoVariant="main" logoBehavior="navigate" isTransparent={false} />

      <PolicyHeader
        title="Monra eSIM Service Terms"
        subtitle="Additional terms for buying and using Monra eSIM connectivity services."
        variant="esim"
        effectiveDate={effectiveDate}
      />

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="legal-card mb-8">
            <h2 className="mb-3 font-sans text-lg font-semibold text-foreground">Related Legal Documents</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Monra eSIM is governed by these product-specific terms plus Monra's
              {" "}
              <Link to="/terms-of-use" className="font-semibold text-esim-deep transition-colors hover:underline">
                Terms of Use
              </Link>
              ,
              {" "}
              <Link to="/privacy-policy" className="font-semibold text-esim-deep transition-colors hover:underline">
                Privacy Policy
              </Link>
              , and
              {" "}
              <Link to="/refund-policy" className="font-semibold text-esim-deep transition-colors hover:underline">
                Refund Policy
              </Link>
              .
            </p>
          </div>

          <div className="legal-card mb-10">
            <h2 className="mb-4 font-sans text-lg font-semibold text-foreground">Table of Contents</h2>
            <ol className="space-y-2 text-sm text-muted-foreground">
              {sections.map((section, index) => (
                <li key={section.id} className="flex gap-3">
                  <span className="w-6 shrink-0 tabular-nums text-foreground/45">{String(index + 1).padStart(2, "0")}</span>
                  <a href={`#${section.id}`} className="transition-colors hover:text-foreground">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-20 border-t border-border py-10">
              <h2 className="mb-3 font-sans text-xl font-semibold tracking-tight text-foreground">{section.title}</h2>
              <p className="leading-relaxed text-muted-foreground">{section.content}</p>
              {"bullets" in section && section.bullets && (
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  {section.bullets.map((bullet, index) => (
                    <li key={index} className="list-disc pl-5">
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              {"email" in section && section.email && (
                <a
                  href={`mailto:${section.email}`}
                  className="mt-2 inline-block font-semibold text-esim-deep transition-colors hover:underline"
                >
                  {section.email}
                </a>
              )}
            </section>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  )
}
